/**
 * usePortfolioSectors - Fetches sector allocation data for the portfolio.
 *
 * Calls GET /api/v1/portfolio/sectors on mount when positions exist.
 * Returns sector breakdown with value, percentage, and position count per sector.
 */

import { useState, useEffect } from 'react';
import { API_BASE_URL } from '@/lib/api';

export interface SectorAllocation {
  sector: string;
  value: number;
  percentage: number;
  count: number;
}

interface UsePortfolioSectorsReturn {
  sectors: SectorAllocation[];
  isLoading: boolean;
}

export function usePortfolioSectors(hasPositions: boolean): UsePortfolioSectorsReturn {
  const [sectors, setSectors] = useState<SectorAllocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasPositions) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20_000);

    setIsLoading(true);
    const token = localStorage.getItem('access_token');
    fetch(`${API_BASE_URL}/api/v1/portfolio/sectors`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: controller.signal,
    })
      .then((r) => {
        if (!r.ok) {
          console.error(`[usePortfolioSectors] HTTP ${r.status}: ${r.statusText}`);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data?.sectors) setSectors(data.sectors);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('[usePortfolioSectors] Failed to fetch sector data:', err);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setIsLoading(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [hasPositions]);

  return { sectors, isLoading };
}
